import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { AuthProvider } from '@shared/auth/AuthContext';
import { initObservability, captureError } from '@shared/lib/observability';
import { RootErrorBoundary } from '@shared/ui';
import { supabase } from '@shared/supabase/client';
import '@shared/i18n'; // side-effect init
import App from './App';
import './styles/global.css';

initObservability({
  release: import.meta.env.VITE_RELEASE as string | undefined,
  environment: import.meta.env.MODE,
});

const queryClient = new QueryClient({
  // Report every query failure (after retries) to telemetry. Many components
  // render `data ?? []`, so a failed fetch is otherwise an invisible blank —
  // this makes the failure visible in the client_errors sink even when the UI
  // degrades silently.
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureError(error, { where: 'query', queryKey: JSON.stringify(query.queryKey).slice(0, 200) });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      // Users on high-latency / flaky links (e.g. KG → our ap-northeast-1
      // region) intermittently lose a response mid-flight: the server logs a
      // clean 200 in <1ms but the browser never receives the body, so the SDK
      // surfaces a network error. retry:1 gave up after ~1s and dead-ended the
      // user (stuck claim/error screens with no auto-recovery). 3 retries with
      // capped exponential backoff (~1s, 2s, 4s) rides out a brief blip.
      retry: 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      // After a long idle, the network may have changed (laptop wake from
      // sleep, wifi reconnect). Refetch so users don't see permanently
      // stuck "Loading…" panels with stale aborted requests.
      refetchOnReconnect: true,
      // Disable TanStack Query's built-in window-focus refetch. We already
      // handle session refresh + query invalidation manually via
      // visibilitychange → getSession() → TOKEN_REFRESHED. Leaving this on
      // causes all queries to refetch on every tab switch, triggering a
      // re-render cascade that can reset form state (e.g. the Add blogger
      // dialog closing or appearing unresponsive after switching tabs).
      refetchOnWindowFocus: false,
    },
  },
});

// Long idle (mac sleep, tab in background for >1h) lets the Supabase JWT
// expire without the SDK's auto-refresh timer firing. First query after
// wake goes out with the stale token → PostgREST 400 → UI stuck on
// "Loading…". Two listeners patch this:
//
//  1. visibilitychange → call getSession() which forces the SDK to check
//     expiry and refresh if needed. Triggers TOKEN_REFRESHED on success.
//  2. onAuthStateChange:TOKEN_REFRESHED → invalidate every cached query
//     so the in-flight components refetch with the fresh token instead
//     of staying stuck on their pre-refresh failure.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      supabase.auth.getSession().catch(() => { /* swallow */ });
    }
  });
}
// Only TOKEN_REFRESHED — Supabase v2 fires SIGNED_IN on every tab focus
// (when getSession() re-resolves the existing session), not just real
// login events. Invalidating every query on SIGNED_IN was causing
// queries to constantly refetch and never settle, leaving loading-gated
// UI (e.g. select dropdowns disabled while categoriesQ.isLoading) stuck
// in a permanent loading state.
supabase.auth.onAuthStateChange((event) => {
  if (event === 'TOKEN_REFRESHED') {
    queryClient.invalidateQueries();
  }
});

// PWA: register the (network-first, same-origin) service worker so the app is
// installable to a phone home screen. Production only — avoids caching noise in dev.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* non-fatal */ });
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  </React.StrictMode>,
);
